/**
 * Empty template component
 */
import Container from '../atoms/container/emptyContainer';
import Focus from '../../../shared/types/enums/focus';
import Modals from '../../../shared/types/enums/modals';
import Text from '../atoms/text/emptyMainText';
import TranslationManager from '../../../shared/assets/locale/translationsManager';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import { clearModal, setModal } from '../../../shared/store/slices/modal.slice';
import { focusSelector } from '../../../shared/store/slices/focus.slice';
import { routerSelector } from '../../../shared/store/slices/router.slice';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { userSelector } from '../../../shared/store/slices/user.slice';

/**
 * Props interface for the empty template
 */
interface Props {
  message?: string;
  modal?: Modals;
}

/**
 * Empty template component. This component renders an empty state with a message.
 * A modal can be displayed if provided.
 */
export default function TEmpty(props: Props) {
  const { text } = useEmpty(props);
  return (
    <Container>
      <Text>{text}</Text>
    </Container>
  );
}

/**
 * Hook to handle the empty template
 */
function useEmpty({ message, modal }: Props) {
  const dispatch = useAppDispatch();
  const { focus } = useAppSelector(focusSelector);
  const { params } = useAppSelector(routerSelector);
  const { isAnonymous } = useAppSelector(userSelector);
  const [displayModal, setDisplayModal] = useState(true);
  const focusRef = useRef(focus);
  const hasLoginRef = useRef<boolean | undefined>(undefined);
  const modalRef = useRef<Modals | undefined>(
    params?.current?.modal as Modals | undefined
  );
  focusRef.current = focus;
  hasLoginRef.current = isAnonymous;
  modalRef.current = modal || (params?.current?.modal as Modals | undefined);

  useEffect(() => {
    return () => {
      dispatch(clearModal());
    };
  }, []);

  useEffect(() => {
    if (displayModal && focus === Focus.MAIN_EMPTY && modalRef.current) {
      dispatch(setModal(modalRef.current));
      setDisplayModal(false);
    }
    if (focus === Focus.MENU) {
      setDisplayModal(true);
    }
  }, [displayModal, focus]);

  useBackKeyboard();

  useKeyboard({
    canBeFocused: false,
    keyDownHandler(key) {
      switch (key) {
        case 'RIGHT':
        case 'SELECT':
          if (modalRef.current) {
            dispatch(setModal(modalRef.current));
          }
          break;
      }
    },
  });

  const { text } = useMemo(() => {
    return {
      text:
        message ??
        (params?.current?.message as string) ??
        TranslationManager.get('PAGES.EMPTY.NO_RESULTS'),
    };
  }, [message, params]);

  return {
    text,
  };
}
